import "./style.scss";

import { Close } from "@mui/icons-material";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardDoubleArrowUpOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowUpOutlined";
import { Tooltip } from "@mui/material";
import classNames from "classnames";
import AddValidators from "components/AddValidators";
import ExitModal from "components/NewForm/ExitModal";
import BlockModal from "components/shared/BlockModal";
import DeleteModal from "components/shared/DeleteModal";
import StyledButton from "components/shared/StyledButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import type { ThreeDotMenuOptions } from "components/shared/ThreeDotMenu";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import { SmallBody } from "helpers/TypographyComponents";
import { formatLocaleDateString, getJsonOrFalse } from "helpers/utils";
import moment from "moment/min/moment-with-locales";
import type { ChangeEvent, RefObject } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router-dom";
import type { AgreementTemplate } from "store/agreementsStore";
import { useAppSelector } from "store/store";
import type { IUser } from "types";

import ContractEditorLexical from "../ContractEditorLexical";

/**
 * Agreement Template Page component
 */
interface IAgreementPage {
    /**
     * Agreement data
     */
    agreement: AgreementTemplate;
    /**
     * If it's the default template
     */
    isDefaultTemplate: boolean;
    /**
     * handleDelete agreement
     */
    handleDelete: (id: number) => Promise<void>;
    /**
     * Create or duplicate template function
     */
    handleNewTemplate: (
        _e: any,
        duplicateData?: any,
        duplicatedAgreementName?: string,
        type?: "agreement" | "avenant" | "framework_contract",
    ) => Promise<void>;
    /**
     * Handle save changes to template
     */
    handleSaveTemplate: (
        data: {
            stringifiedBlocks: string;
            html: string;
        },
        name: string,
        id: number,
        isLexical?: boolean,
    ) => void;
    /**
     * Handle close Statement template page (drawer)
     */
    handleClose: () => void;
    /**
     * Company sttg
     */
    usesValidationChain: boolean;
    /**
     * Whether the company has reached its agreement templates limit
     */
    isTemplateLimitReached: boolean;
    /**
     * Validators list
     */
    validators: IUser[];
    /**
     * Ref of the scrolling page with agreement template
     */
    agreementPageRef: RefObject<HTMLDivElement>;
    /**
     * If the drawer is open
     */
    isDrawerOpen: boolean;
}
const iconSx = { width: "16px", height: "16px", color: "var(--foreground)" };

const AgreementPage = ({
    agreement,
    isDefaultTemplate,
    handleDelete,
    handleNewTemplate,
    handleSaveTemplate,
    handleClose,
    usesValidationChain,
    isTemplateLimitReached,
    validators,
    agreementPageRef,
    isDrawerOpen,
}: IAgreementPage) => {
    const { savedEditorContent } = useAppSelector((state) => state.agreements);
    const [agreementNameValue, setAgreementNameValue] = useState(agreement?.name);
    const [showNameField, setShowNameField] = useState<number | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showScrollTopBtn, setShowScrollTopBtn] = useState(false);
    const isEditingName = showNameField === agreement?.id;
    const [showExitModal, setShowExitModal] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const templatePageHeaderRef = useRef<HTMLDivElement>(null);
    const [t] = useTranslation();
    const lang = getJsonOrFalse(localStorage.getItem("settings") ?? "{}").accountSettings?.language || "fr";

    /**
     * Handle input change name of the agreement
     */
    const handleChangeName = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setAgreementNameValue(e.target.value);
        setIsDirty(true);
    };

    const onPressEnter = (e) => {
        if (e.keyCode === 13) setShowNameField(null);
    };

    const onEditName = (e: any) => {
        e.stopPropagation();
        if (!isEditingName) setShowNameField(agreement.id);
        else setShowNameField(null);
    };

    const actionsOptions: ThreeDotMenuOptions[] = [
        {
            id: "duplicate",
            name: t("ContractSettings.DuplicateTemplate"),
            action: (e) => {
                handleNewTemplate && handleNewTemplate(e, savedEditorContent, agreementNameValue, agreement.type);
            },
            disabled: isTemplateLimitReached,
            tooltip: isTemplateLimitReached ? t("ContractSettings.TemplateLimitReached") : "",
        },
        {
            id: "delete",
            name: t("ContractSettings.DeleteTemplate"),
            action: () => {
                setShowDeleteModal(true);
            },
            disabled: isDefaultTemplate,
            tooltip: isDefaultTemplate ? t("ContractSettings.CannotBeDeleted") : "",
        },
    ];

    /**
     * Scroll to the top of the agreement edit page
     */
    const handleScrollTop = () => {
        const scrollContainer = agreementPageRef?.current;
        if (scrollContainer) {
            scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    /**
     * Show scroll top btn
     * when header is stick to the top of the page
     */
    useEffect(() => {
        const handleScroll = () => {
            if (templatePageHeaderRef.current) {
                const toolbarTop = templatePageHeaderRef.current.getBoundingClientRect().top;
                setShowScrollTopBtn(toolbarTop <= 0);
            }
        };
        const scrollContainer = agreementPageRef?.current;
        if (scrollContainer) {
            scrollContainer.addEventListener("scroll", handleScroll);
        }
        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener("scroll", handleScroll);
            }
        };
    }, []);

    /** Close drawer */
    const onClose = () => {
        if (isDirty) {
            setShowExitModal(true);
        } else {
            handleClose();
            setIsDirty(false);
        }
    };

    /**
     * Delete agreement function
     */
    const deleteAgreement = async () => {
        await handleDelete(agreement.id);
        handleClose();
        setIsDirty(false);
    };

    return (
        <div className="agreement-template-page" ref={agreementPageRef}>
            <DeleteModal
                open={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                }}
                onDelete={deleteAgreement}
                elementTitle={agreementNameValue || agreement?.name}
                description={`${t("ContractSettings.DeleteWarning1")}. ${t("ContractSettings.DeleteWarning2")}`}
            />
            <div onClick={handleScrollTop} className={classNames({ "scroll-top-btn": true, show: showScrollTopBtn })}>
                <KeyboardDoubleArrowUpOutlinedIcon sx={{ width: "22px" }} />
            </div>
            <div className="agreement-template-page-header" ref={templatePageHeaderRef}>
                <div className="agreement-name-wrapper">
                    {isEditingName ? (
                        <StyledTextField
                            onClick={(e) => e.stopPropagation()}
                            value={agreementNameValue}
                            onChange={handleChangeName}
                            onKeyDown={onPressEnter}
                            className="agreement-name-field"
                            onBlur={() => setShowNameField(null)}
                        />
                    ) : (
                        <Tooltip title={isDefaultTemplate && t("ContractSettings.CannotChangeTitle")}>
                            <h2
                                className={classNames({ "agreement-name": true, default: isDefaultTemplate })}
                                onClick={!isDefaultTemplate ? onEditName : () => {}}
                            >
                                {agreementNameValue}
                            </h2>
                        </Tooltip>
                    )}
                    {!isDefaultTemplate && (
                        <div className="agreement-name-edit" onClick={onEditName}>
                            {showNameField ? <DoneOutlinedIcon sx={iconSx} /> : <EditOutlinedIcon sx={iconSx} />}
                        </div>
                    )}
                    <span className={`agreement-page-type-badge ${agreement.type ?? ""}`}>
                        {agreement.type === "avenant" && t("ContractSettings.TypeAvenant")}
                        {agreement.type === "framework_contract" && t("ContractSettings.TypeFrameworkContract")}
                        {(!agreement.type || agreement.type === "agreement") && t("ContractSettings.TypeAgreement")}
                    </span>
                </div>
                <div className="contract-head-action">
                    <ThreeDotMenu
                        options={actionsOptions}
                        style={{ height: "31px", width: "31px", backgroundColor: "var(--backgroundZ0)" }}
                    />
                    <StyledButton
                        buttonType="primary"
                        sx={{ width: "14rem !important" }}
                        className="contract-save-btn-agreement"
                        onClick={() => {
                            handleSaveTemplate(savedEditorContent, agreementNameValue, agreement.id, true),
                                setIsDirty(false);
                        }}
                    >
                        {t("ContractSettings.Save")}
                    </StyledButton>
                    <StyledButton buttonType="secondary" onClick={onClose} className="quit-btn">
                        {t("ContractSettings.Quit")}
                        <Close sx={{ height: "18px", width: "18px", color: "var(--foreground)" }} />
                    </StyledButton>
                </div>
            </div>
            <div className="agreement-template-page-wrapper">
                {usesValidationChain && <AddValidators templateId={agreement.id} validators={validators} />}
                <div className="modified-on">
                    <Tooltip
                        title={formatLocaleDateString({
                            date: agreement.modified,
                            options: { numeric: true, timeZone: "UTC" },
                        })}
                    >
                        <div>
                            <SmallBody color="grey">
                                {t("ContractSettings.LastModified")}{" "}
                                {`${moment(agreement.modified).locale(lang).fromNow()}`}
                            </SmallBody>
                        </div>
                    </Tooltip>
                </div>
                <ContractEditorLexical
                    agreement={agreement}
                    setIsDirty={setIsDirty}
                    isDrawerOpen={isDrawerOpen}
                    isAgreementSettings
                />
            </div>
            <ExitModal
                open={showExitModal}
                onCancel={() => setShowExitModal(false)}
                onConfirm={() => {
                    setShowExitModal(false);
                    handleClose();
                }}
            />
        </div>
    );
};

export default AgreementPage;
